"use client"

import { useId, useRef, useState } from "react"
import { MeetingShare } from "@/schema"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { Button, Input } from "@ui/shad"
import { format, isToday } from "date-fns"
import { Check, Copy, Eye, Mail, Share2, Trash2 } from "lucide-react"
import { toast } from "sonner"

import { useCreateMeetingShare, useMeetingShares, useRevokeMeetingShare } from "@/hooks/api/meeting-shares"
import { SimpleFormDialog } from "@/components/dialogs"
import { ComposeEmail } from "@/components/email/compose"

interface ShareMeetingDialogProps {
  meetingId: string
  meetingTitle?: string
  meetingStartTime?: string | Date
}

function buildShareDateLabel(meetingStartTime?: string | Date): string {
  if (!meetingStartTime) return "our recent meeting"

  const parsedDate = new Date(meetingStartTime)
  if (Number.isNaN(parsedDate.getTime())) return "our recent meeting"

  return isToday(parsedDate) ? "today" : `on ${format(parsedDate, "MMMM d, yyyy")}`
}

export default function ShareMeetingDialog({ meetingId, meetingTitle, meetingStartTime }: ShareMeetingDialogProps) {
  const formId = useId()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const isOpeningEmailDialogRef = useRef(false)

  const { data: sharesData } = useMeetingShares(meetingId)
  const createShare = useCreateMeetingShare()
  const revokeShare = useRevokeMeetingShare()
  const openDialog = useGlobalDialogStore((s) => s.openDialog)

  const shares = sharesData?.filter((s) => !s.revoked_at) ?? []

  function handleCreateShare() {
    if (!email.trim() || createShare.isPending) return
    createShare.mutate(
      {
        meetingId,
        email: email.trim(),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
      },
      {
        onSuccess: (share) => {
          setEmail("")
          setFirstName("")
          setLastName("")
          toast.success(`Meeting shared with ${share.email}`)
        },
      }
    )
  }

  function handleCopyLink(share: MeetingShare) {
    navigator.clipboard.writeText(share.portal_url)
    setCopiedToken(share.token)
    toast.success("Link copied to clipboard")
    setTimeout(() => setCopiedToken(null), 2000)
  }

  function handleSendEmail(share: MeetingShare) {
    const subject = `Meeting recording: ${meetingTitle || "Meeting"}`
    const greetingName = share.first_name?.trim() || "there"
    const meetingDateText = buildShareDateLabel(meetingStartTime)
    const body = `<p>Hi ${greetingName},</p><br><p>I wanted to share the recording to our recent meeting ${meetingDateText}. Here is the link, you'll just verify your email to get access.</p><br><p><a href="${share.portal_url}" target="_blank" rel="noopener noreferrer">View meeting recording</a></p><br><p>Thanks!</p>`

    isOpeningEmailDialogRef.current = true
    setOpen(false)

    window.setTimeout(() => {
      openDialog({
        variant: "custom",
        content: (
          <ComposeEmail
            toEmails={[{ email: share.email, name: share.first_name || null, type: null }]}
            startingSubject={subject}
            startingHTML={body}
          />
        ),
      })
      window.setTimeout(() => {
        isOpeningEmailDialogRef.current = false
      }, 1000)
    }, 0)
  }

  function handleRevoke(shareId: string) {
    revokeShare.mutate({ meetingId, shareId })
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Share2 />
        Share
      </Button>
      <SimpleFormDialog
        open={open}
        onOpenChange={setOpen}
        title="Share meeting"
        description="Share this meeting recording with a client or contact."
        onCloseAutoFocus={(event) => {
          if (isOpeningEmailDialogRef.current) {
            event.preventDefault()
            isOpeningEmailDialogRef.current = false
          }
        }}
        action={{ label: "Share", form: formId, disabled: !email.trim(), pending: createShare.isPending }}
      >
        <div className="space-y-2">
          <form
            id={formId}
            onSubmit={(event) => {
              event.preventDefault()
              handleCreateShare()
            }}
            className="grid gap-2"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              <Input
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={createShare.isPending}
              />
              <Input
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={createShare.isPending}
              />
            </div>
            <Input
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={createShare.isPending}
            />
          </form>

          {shares.length > 0 && (
            <div className="space-y-2">
              <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Shared with</p>
              <div className="divide-y rounded-md border">
                {shares.map((share) => (
                  <div key={share.id} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {share.first_name || share.last_name
                          ? `${share.first_name || ""} ${share.last_name || ""}`.trim()
                          : share.email}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">{share.email}</p>
                      <div className="text-muted-foreground flex items-center gap-2 text-xs">
                        <span className="flex items-center gap-1">
                          <Eye className="size-3" />
                          {share.view_count} {share.view_count === 1 ? "view" : "views"}
                        </span>
                        {share.last_viewed_at && (
                          <span>Last viewed {format(new Date(share.last_viewed_at), "MMM d")}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="iconXs" onClick={() => handleCopyLink(share)} title="Copy link">
                        {copiedToken === share.token ? <Check className="text-green-accent" /> : <Copy />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="iconXs"
                        onClick={() => handleSendEmail(share)}
                        title="Send via email"
                      >
                        <Mail />
                      </Button>
                      <Button
                        variant="ghost"
                        size="iconXs"
                        onClick={() => handleRevoke(share.id)}
                        title="Revoke access"
                        disabled={revokeShare.isPending}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </SimpleFormDialog>
    </>
  )
}
